import {
  ConditionComparator,
  FieldOption,
  FormFieldCondition,
  FormFieldConditionForm,
  FormFieldConditionJourney,
  FormFieldValue,
} from '@components/form/v2';
import {
  FieldMappingFieldItem,
  FieldMappingGroup,
  FieldType,
} from '@components/text-editors/addons/field-mapping';
import { findVariableField } from '@components/text-editors/addons/field-mapping-variable-label';

// Type-only: a runtime import of ./context would close a module cycle through
// create-builder-store back into this file.
import type { MarketplaceIntegrationWithLists, PredefinedOptionsLists } from './context';

export type ConditionDraftForm = {
  source: 'form';
  id: string;
  questionId?: string;
  comparator?: ConditionComparator;
  value?: FormFieldValue;
};

export const MULTI_COMPARATORS: ReadonlySet<ConditionComparator> = new Set(['any_of', 'none_of']);

export const isMultiComparator = (comparator: ConditionComparator): boolean =>
  MULTI_COMPARATORS.has(comparator);

const VALUELESS_COMPARATORS = new Set<string>(['empty', 'nempty']);

const comparatorRequiresValue = (comparator: ConditionComparator): boolean =>
  !VALUELESS_COMPARATORS.has(comparator);

export const hasValue = (value: FormFieldValue | undefined): boolean => {
  if (value === undefined || value === null || value === '') return false;
  if (Array.isArray(value) && value.length === 0) return false;
  return true;
};

export const canSaveCondition = (condition: ConditionDraftForm): boolean => {
  if (!condition.questionId) return false;
  if (!condition.comparator) return false;
  if (comparatorRequiresValue(condition.comparator) && !hasValue(condition.value)) {
    return false;
  }
  return true;
};

const reshapeValueForComparator = (
  value: FormFieldValue | undefined,
  willBeMulti: boolean
): FormFieldValue => {
  if (willBeMulti) {
    if (Array.isArray(value)) return value;
    return hasValue(value) ? [value as string | number] : [];
  }
  if (Array.isArray(value)) return value[0] ?? '';
  return value ?? '';
};

export const applyComparatorChange = <T extends FormFieldCondition>(
  condition: T,
  next: ConditionComparator
): T => {
  if (next === condition.comparator) return condition;
  const wasMulti = isMultiComparator(condition.comparator);
  const willBeMulti = isMultiComparator(next);
  if (wasMulti === willBeMulti) {
    return { ...condition, comparator: next };
  }
  return {
    ...condition,
    comparator: next,
    value: reshapeValueForComparator(condition.value, willBeMulti),
  };
};

const toggleMultiValue = (
  current: FormFieldValue | undefined,
  candidate: string
): Array<string | number> => {
  const arr = Array.isArray(current) ? current : [];
  return arr.includes(candidate) ? arr.filter((entry) => entry !== candidate) : [...arr, candidate];
};

export const applyValueChange = <T extends FormFieldCondition>(
  condition: T,
  next: FormFieldValue
): T => {
  if (!isMultiComparator(condition.comparator) || typeof next !== 'string') {
    return { ...condition, value: next };
  }
  return { ...condition, value: toggleMultiValue(condition.value, next) };
};

export const isCompleteCondition = (condition: FormFieldCondition): boolean => {
  if (condition.source === 'form' && !condition.questionId) return false;
  if (condition.source === 'journey' && !condition.variable) return false;
  if (!condition.comparator) return false;
  const valueIsArray = Array.isArray(condition.value);
  const expectArray = isMultiComparator(condition.comparator);
  if (expectArray !== valueIsArray) return false;
  if (comparatorRequiresValue(condition.comparator) && !hasValue(condition.value)) {
    return false;
  }
  return true;
};

export const getRenderableConditionCount = (conditions: FormFieldCondition[]): number =>
  conditions.length;

const optionValueOf = (entry: FormFieldValue | null | undefined): string | undefined => {
  if (entry === null || entry === undefined || entry === '') return undefined;
  return String(entry);
};

/**
 * Values a condition references that are no longer present in the field's current
 * option set. A non-empty result means the condition can never match: the option it
 * was built against was replaced (e.g. the field was switched from manually-entered
 * options to a predefined list, which swaps every option's underlying value).
 */
export const getMissingOptionValues = (
  value: FormFieldValue | null | undefined,
  options: ReadonlyArray<{ value: string }> | null
): string[] => {
  if (options === null) return [];
  const knownValues = new Set(options.map((option) => option.value));
  const entries = Array.isArray(value) ? value : [value];
  const missing = entries
    .map(optionValueOf)
    .filter((entry): entry is string => entry !== undefined && !knownValues.has(entry));
  return [...new Set(missing)];
};

export type ResolvedFieldOptions = {
  options: FieldOption[];
  /** False when the referenced list is in none of the loaded sources — not yet
   * loaded or deleted. Callers must not treat the stored value as broken then. */
  isResolved: boolean;
};

/**
 * The single definition of a field's effective option set. Hidden lists back
 * element-local options, so while editing the field's own array is the live
 * truth and the list is only the server's last-saved snapshot.
 */
export const resolveFieldOptions = (
  field: {
    options: FieldOption[];
    optionsFromListId?: string | null;
    excludedOptions?: string[] | null;
  },
  predefinedOptionsLists: PredefinedOptionsLists,
  marketplaceIntegrationLists: MarketplaceIntegrationWithLists[] = []
): ResolvedFieldOptions => {
  if (!field.optionsFromListId) {
    return { options: field.options, isResolved: true };
  }

  const excludedOptions = field.excludedOptions ?? [];
  const withoutExcluded = (options: FieldOption[]): FieldOption[] => {
    return options.filter((option) => !excludedOptions.includes(option.value));
  };

  const predefinedList = predefinedOptionsLists.find(
    (list) => list.value === field.optionsFromListId
  );
  if (predefinedList) {
    if (predefinedList.isHidden) {
      return { options: field.options, isResolved: true };
    }
    return { options: withoutExcluded(predefinedList.options), isResolved: true };
  }

  for (const integration of marketplaceIntegrationLists) {
    const marketplaceList = integration.lists.find(
      (list) => list.shortId === field.optionsFromListId
    );
    if (marketplaceList) {
      return { options: withoutExcluded(marketplaceList.options), isResolved: true };
    }
  }

  return { options: field.options, isResolved: false };
};

export const journeyVariableGroupsAreNonEmpty = (
  groups: ReadonlyArray<FieldMappingGroup>
): boolean => groups.some((group) => group.fields.length > 0);

export const findCanonicalVariable = (
  groups: FieldMappingGroup[],
  variable: FieldMappingFieldItem
): FieldMappingFieldItem | undefined => {
  return findVariableField(
    variable,
    groups.flatMap((group) => group.fields)
  );
};

const SELECT_LIKE_VARIABLE_TYPES: ReadonlySet<FieldType> = new Set([
  'select',
  'multi_select',
  'options',
]);

/**
 * Stored values of a journey-source condition that no longer exist among the
 * source variable's current options. The stored variable carries an options
 * snapshot from pick time, so staleness can only be judged against the
 * canonical variable resolved from the live variable groups — no canonical
 * variable, or an option set that has not resolved yet, is not proof of
 * anything and reports nothing.
 */
export const getStaleJourneyConditionValues = (
  condition: FormFieldConditionJourney,
  canonicalVariable: FieldMappingFieldItem | undefined,
  currentOptions: ReadonlyArray<{ value: string }>
): string[] => {
  if (!canonicalVariable?.type || !SELECT_LIKE_VARIABLE_TYPES.has(canonicalVariable.type)) {
    return [];
  }
  if (currentOptions.length === 0) return [];
  return getMissingOptionValues(condition.value, currentOptions);
};

const TEXT_LIKE_COMPARATORS: ReadonlyArray<ConditionComparator> = [
  'is',
  'not_is',
  'starts',
  'not_starts',
  'contains',
  'not_contains',
];

const FORM_COMPARATORS_BY_FIELD_TYPE: Partial<
  Record<FieldType, ReadonlyArray<ConditionComparator>>
> = {
  select: ['is', 'not_is', 'any_of', 'none_of'],
  multi_select: ['any_of', 'none_of'],
  options: ['is', 'not_is', 'any_of', 'none_of'],
  date: ['before', 'on', 'after'],
  text: TEXT_LIKE_COMPARATORS,
  email: TEXT_LIKE_COMPARATORS,
  phone: TEXT_LIKE_COMPARATORS,
  paragraph: TEXT_LIKE_COMPARATORS,
  number: ['is', 'not_is'],
  decimal: ['is', 'not_is'],
  boolean: ['is', 'not_is'],
  kennitala: TEXT_LIKE_COMPARATORS,
};

export const getComparatorsForFieldType = (
  fieldType: FieldType | undefined
): ReadonlyArray<ConditionComparator> => {
  if (!fieldType) return [];
  return FORM_COMPARATORS_BY_FIELD_TYPE[fieldType] ?? [];
};

export const buildResetFormCondition = (
  conditionId: string,
  newQuestionId: string,
  availableComparators: ReadonlyArray<ConditionComparator>
): FormFieldConditionForm => {
  const comparator = availableComparators[0] ?? 'is';
  return {
    source: 'form',
    id: conditionId,
    questionId: newQuestionId,
    comparator,
    value: isMultiComparator(comparator) ? [] : '',
  };
};

export const buildResetJourneyCondition = (
  conditionId: string,
  variable: FieldMappingFieldItem,
  availableComparators: ReadonlyArray<ConditionComparator>
): FormFieldConditionJourney => {
  const comparator = availableComparators[0] ?? 'is';
  return {
    source: 'journey',
    id: conditionId,
    variable,
    comparator,
    value: isMultiComparator(comparator) ? [] : '',
  };
};
